import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/auth/auth-context";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address")
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordProps {
  onBack?: () => void;
  onSuccess?: () => void;
}

export const ForgotPassword = ({ onBack, onSuccess }: ForgotPasswordProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { sendPasswordResetEmail } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema)
  });

  const handlePasswordReset = async (data: ForgotPasswordForm) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await sendPasswordResetEmail(data.email);
      setSuccess("Password reset email sent! Check your email for instructions.");
      onSuccess?.();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to send password reset email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 text-center">
        <h1 className="text-xl font-semibold text-gray-900">
          <b>Tigim HQ</b> Forgot Password
        </h1>
      </div>

      <div className="mb-4 text-center text-base text-gray-600">
        we can help. Enter your email address below and we'll send you a link to reset your
        password.
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <form
        autoComplete="off"
        onSubmit={handleSubmit(handlePasswordReset)}
        className="space-y-3"
        aria-label="Forgot password form"
      >
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            className="w-full h-9"
            required
            disabled={isLoading}
            aria-describedby="email-description"
          />
          <div id="email-description" className="sr-only">
            Enter your email address
          </div>
          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
        </div>

        <Button
          type="submit"
          className="w-full h-10 text-base bg-purple-600 hover:bg-[#0f172a] text-white"
          disabled={isLoading}
          aria-label="Send password reset email"
        >
          {isLoading ? "Sending..." : "Send"}
        </Button>
      </form>

      <div className="text-center mt-4">
        <Button variant="link" onClick={onBack} disabled={isLoading} className="h-5">
          Return to Login
        </Button>
      </div>
    </div>
  );
};
